import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import TicketHistory from './TicketHistory';
import { useGlobalContext } from '../../data/GlobalContext';
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion';

export const JILI_FORTUNE_RUSH_TIERS = [
    {
        tier: 1,
        turnoverMin: 1000,
        turnoverMax: 14999,
        depositTarget: 200,
        raffleTickets: 1,
    },
    {
        tier: 2,
        turnoverMin: 15000,
        turnoverMax: 49999,
        depositTarget: 3000,
        raffleTickets: 5,
    },
    {
        tier: 3,
        turnoverMin: 50000,
        turnoverMax: 149999,
        depositTarget: 10000,
        raffleTickets: 15,
    },
    {
        tier: 4,
        turnoverMin: 150000,
        turnoverMax: 499999,
        depositTarget: 30000,
        raffleTickets: 50,
    },
    {
        tier: 5,
        turnoverMin: 500000,
        turnoverMax: Infinity,
        depositTarget: 100000,
        raffleTickets: 100,
    },
];

const formatNumber = (value = 0) => {
    return Number(value || 0).toLocaleString('en-US');
};

const getPercentage = (current = 0, total = 0) => {
    const currentValue = Number(current || 0);
    const totalValue = Number(total || 0);

    if (!totalValue) return 0;

    return Math.min(
        100,
        Math.max(0, Math.round((currentValue / totalValue) * 100))
    );
};

const ProgressCard = forwardRef(
    (
        { icon, title, current, total, percentage, needed, iconClass = '' },
        ref
    ) => {
        return (
            <div ref={ref} className="stat-card progress-card">
                <div className={`jili-ticket ${iconClass}`}>
                    <img src={icon} alt="" />
                </div>

                <div className="stat-content">
                    <div className="stat-title">{title}</div>

                    <div className="stat-value">
                        <span className="highlight">
                            {formatNumber(current)}
                        </span>

                        <span className="divider"> / </span>

                        <span>{formatNumber(total)}</span>
                    </div>

                    <div className="stat-action">
                        <p>{formatNumber(needed || 0)} more to unlock</p>
                    </div>

                    <div className="progress-row">
                        <div className="progress-track">
                            <motion.div
                                className="progress-fill"
                                initial={false}
                                animate={{
                                    width: `${percentage}%`,
                                }}
                                transition={{
                                    duration: 0.5,
                                    ease: 'easeOut',
                                }}
                            />
                        </div>

                        <span className="progress-percent">{percentage}%</span>
                    </div>
                </div>
            </div>
        );
    }
);

const RaffleTickets = ({
    tickets,
    earnedToday,
    targetRef,
    targetControls,
    rewardFlash,
    onShowTicket,
}) => {
    return (
        <div className="stat-card status-card">
            <motion.div
                ref={targetRef}
                className="jili-ticket ticket-animation-target"
                animate={targetControls}
            >
                <img
                    src="/images/game/jili-rush/JILI-TICKET.webp"
                    alt="Raffle ticket"
                />

                <AnimatePresence>
                    {rewardFlash > 0 && (
                        <motion.div
                            className="ticket-earned-popup"
                            initial={{
                                opacity: 0,
                                scale: 0.4,
                                y: 5,
                            }}
                            animate={{
                                opacity: 1,
                                scale: 1,
                                y: -10,
                            }}
                            exit={{
                                opacity: 0,
                                scale: 0.8,
                                y: -25,
                            }}
                            transition={{
                                duration: 0.35,
                            }}
                        >
                            +{rewardFlash}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            <div className="stat-content status-content">
                <div className="stat-title">Tickets Earned</div>

                <div className="status-value">
                    <motion.span
                        key={tickets}
                        className="highlight"
                        initial={{
                            scale: 1,
                        }}
                        animate={{
                            scale: [1, 1.08, 1],
                        }}
                        transition={{
                            duration: 0.4,
                        }}
                    >
                        {formatNumber(tickets)} Raffle{' '}
                        {tickets <= 1 ? 'Ticket' : 'Tickets'}
                    </motion.span>

                    <span className="earn-today">
                        +{formatNumber(earnedToday)} earned today
                    </span>

                    <div className="stat-content--btn">
                        <button onClick={onShowTicket}>View Ticket</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function StatsPanel() {
    const { jiliFortuneRushInfo, jiliFortuneRushLoading } = useGlobalContext();

    const [showTable, setShowTable] = useState(false);
    const [showTicket, setShowTicket] = useState(false);

    const depositAmount = Number(jiliFortuneRushInfo?.depositAmount ?? 0);

    const turnoverAmount = Number(jiliFortuneRushInfo?.turnover ?? 0);

    const raffleTickets = Number(
        jiliFortuneRushInfo?.totalTickets ?? jiliFortuneRushInfo?.ticket ?? 0
    );

    const earnedToday = Number(
        jiliFortuneRushInfo?.raffleTicketsEarnedToday ?? 0
    );

    /*
     * Find the first tier that has NOT been completed yet.
     *
     * A tier is completed only when BOTH:
     * - deposit requirement is reached
     * - turnover requirement is reached
     */
    const getCurrentJiliTier = (deposit = 0, turnover = 0) => {
        const depositValue = Number(deposit || 0);
        const turnoverValue = Number(turnover || 0);

        const nextTier = JILI_FORTUNE_RUSH_TIERS.find((tier) => {
            const depositComplete = depositValue >= tier.depositTarget;

            const turnoverComplete = turnoverValue >= tier.turnoverMin;

            return !depositComplete || !turnoverComplete;
        });

        /*
         * If every tier is already completed,
         * stay on Tier 5.
         */
        return (
            nextTier ||
            JILI_FORTUNE_RUSH_TIERS[JILI_FORTUNE_RUSH_TIERS.length - 1]
        );
    };

    const currentTier = getCurrentJiliTier(depositAmount, turnoverAmount);
    const tier = currentTier.tier;

    const depositTarget = currentTier.depositTarget;
    const turnoverTarget = currentTier.turnoverMin;
    const missionRewardTickets = currentTier.raffleTickets;

    const isDataLoaded =
        !jiliFortuneRushLoading && jiliFortuneRushInfo !== null;

    const missionSourceRef = useRef(null);
    const raffleTargetRef = useRef(null);
    const raffleSectionRef = useRef(null);
    const animationRunningRef = useRef(false);
    const missionAnimatedRef = useRef(false);

    const flashTimeoutRef = useRef(null);

    const [flyingTickets, setFlyingTickets] = useState([]);
    const [rewardFlash, setRewardFlash] = useState(0);

    const targetControls = useAnimationControls();

    const depositPercentage = getPercentage(depositAmount, depositTarget);

    const turnoverPercentage = getPercentage(turnoverAmount, turnoverTarget);

    const depositNeeded = Math.max(depositTarget - depositAmount, 0);

    const turnoverNeeded = Math.max(turnoverTarget - turnoverAmount, 0);

    const missionComplete =
        isDataLoaded &&
        depositAmount >= depositTarget &&
        turnoverAmount >= turnoverTarget;

    const finishTicketAnimation = useCallback(() => {
        setFlyingTickets([]);

        animationRunningRef.current = false;

        /*
         * Show reward amount near raffle ticket.
         */
        setRewardFlash(Number(missionRewardTickets || 0));

        /*
         * Destination ticket impact/bounce.
         */
        targetControls.start({
            scale: [1, 1.3, 0.92, 1.08, 1],
            rotate: [0, -6, 6, -3, 0],

            transition: {
                duration: 0.55,
                ease: 'easeOut',
            },
        });

        /*
         * MOBILE ONLY:
         * Scroll to raffle ticket section after ticket arrives.
         */
        const isMobile = window.matchMedia('(max-width: 839px)').matches;

        if (isMobile && raffleSectionRef.current) {
            setTimeout(() => {
                raffleSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 250);
        }

        if (flashTimeoutRef.current) {
            clearTimeout(flashTimeoutRef.current);
        }

        flashTimeoutRef.current = setTimeout(() => {
            setRewardFlash(0);
        }, 1400);
    }, [missionRewardTickets, targetControls]);

    const playTicketAnimation = useCallback(() => {
        if (
            !missionSourceRef.current ||
            !raffleTargetRef.current ||
            animationRunningRef.current
        ) {
            return false;
        }

        const sourceRect = missionSourceRef.current.getBoundingClientRect();

        const targetRect = raffleTargetRef.current.getBoundingClientRect();

        /*
         * Maximum of 5 visual tickets.
         *
         * Even if reward is 15 / 50 / 100,
         * don't render 100 images.
         */
        const visualTicketCount = Math.min(
            Math.max(Number(missionRewardTickets) || 1, 1),
            5
        );

        const ticketSize = 60;

        const startX = sourceRect.left + sourceRect.width / 2 - ticketSize / 2;

        const startY = sourceRect.top + sourceRect.height / 2 - ticketSize / 2;

        const targetX = targetRect.left + targetRect.width / 2 - ticketSize / 2;

        const targetY = targetRect.top + targetRect.height / 2 - ticketSize / 2;

        const timestamp = Date.now();

        animationRunningRef.current = true;

        setFlyingTickets(
            Array.from(
                {
                    length: visualTicketCount,
                },
                (_, index) => ({
                    id: `${timestamp}-${index}`,
                    index,
                    startX,
                    startY,
                    targetX,
                    targetY,
                })
            )
        );

        return true;
    }, [missionRewardTickets]);

    /*
     * Detect:
     *
     * incomplete
     *      ↓
     * deposit + turnover API succeeds
     *      ↓
     * both become complete
     *      ↓
     * play animation ONCE
     */
    useEffect(() => {
        if (!isDataLoaded) return;

        /*
         * Mission was reset / new daily mission.
         * Allow animation again next time it completes.
         */
        if (!missionComplete) {
            missionAnimatedRef.current = false;
            return;
        }

        if (missionComplete && !missionAnimatedRef.current && !showTable) {
            const frame = requestAnimationFrame(() => {
                const animationStarted = playTicketAnimation();

                if (animationStarted) {
                    missionAnimatedRef.current = true;
                }
            });

            return () => cancelAnimationFrame(frame);
        }
    }, [missionComplete, isDataLoaded, showTable, playTicketAnimation]);

    useEffect(() => {
        return () => {
            if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
            }
        };
    }, []);

    const handleShowTicket = () => {
        setShowTicket(true);
    };

    const handleShowTable = () => {
        setShowTable(true);
    };

    const handleCloseTable = () => {
        setShowTable(false);
    };
    const handleCloseTicket = () => {
        setShowTicket(false);
    };
    const tabVariants = {
        initial: {
            opacity: 0,
            y: 15,
        },

        animate: {
            opacity: 1,
            y: 0,
        },

        exit: {
            opacity: 0,
            y: -15,
        },
    };

    return (
        <>
            {/* ========================================= */}
            {/* FLYING TICKET ANIMATION */}
            {/* ========================================= */}

            <AnimatePresence>
                {flyingTickets.map((ticket, index) => {
                    /*
                     * Slight curve difference for every
                     * ticket so they don't overlap perfectly.
                     */
                    const curveOffset =
                        index % 2 === 0 ? -35 - index * 5 : 35 + index * 5;

                    const middleX =
                        (ticket.startX + ticket.targetX) / 2 + curveOffset;

                    const middleY =
                        Math.min(ticket.startY, ticket.targetY) -
                        110 -
                        index * 10;

                    return (
                        <motion.img
                            key={ticket.id}
                            src="/images/game/jili-rush/JILI-TICKET.webp"
                            alt=""
                            className="earned-ticket-flying"
                            initial={{
                                x: ticket.startX,
                                y: ticket.startY,
                                opacity: 0,
                                scale: 0.3,
                                rotate: index % 2 === 0 ? -15 : 15,
                            }}
                            animate={{
                                x: [ticket.startX, middleX, ticket.targetX],

                                y: [ticket.startY, middleY, ticket.targetY],

                                scale: [0.3, 1, 0.35],

                                opacity: [0, 1, 1],

                                rotate: [
                                    index % 2 === 0 ? -15 : 15,

                                    index % 2 === 0 ? 12 : -12,

                                    0,
                                ],
                            }}
                            transition={{
                                duration: 0.85,
                                delay: index * 0.1,
                                times: [0, 0.5, 1],
                                ease: 'easeInOut',
                            }}
                            onAnimationComplete={
                                index === flyingTickets.length - 1
                                    ? finishTicketAnimation
                                    : undefined
                            }
                        />
                    );
                })}
            </AnimatePresence>

            {/* ========================================= */}
            {/* TIER TABLE */}
            {/* ========================================= */}

            {showTable && (
                <motion.div
                    variants={tabVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{
                        duration: 0.2,
                    }}
                >
                    <div className="fr-tnc-table-wrapper fr-stats-panel">
                        <table className="fr-tnc-table">
                            <thead>
                                <tr>
                                    <th>Tier</th>
                                    <th>Daily Turnover Target</th>
                                    <th>Target Deposit</th>
                                    <th>Raffle Entries</th>
                                </tr>
                            </thead>

                            <tbody>
                                {JILI_FORTUNE_RUSH_TIERS.map((item) => (
                                    <tr key={item.tier}>
                                        <td>{item.tier}</td>

                                        <td width={150}>
                                            {item.turnoverMin.toLocaleString()}{' '}
                                            -{' '}
                                            {item.turnoverMax === Infinity
                                                ? 'above'
                                                : item.turnoverMax.toLocaleString()}
                                        </td>

                                        <td>
                                            {item.depositTarget.toLocaleString()}
                                        </td>

                                        <td>
                                            🎫 {item.raffleTickets}{' '}
                                            {item.raffleTickets === 1
                                                ? 'Ticket'
                                                : 'Tickets'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="stats-panel-table">
                        <button onClick={handleCloseTable}>
                            <FontAwesomeIcon
                                icon={faArrowLeft}
                                className="fa-deposit-icon"
                            />{' '}
                            Back
                        </button>
                    </div>
                </motion.div>
            )}

            {/* ========================================= */}
            {/* PROGRESS */}
            {/* ========================================= */}

            {!showTable && !showTicket && (
                <>
                    <div className="stats-panel">
                        <div className="ticket-history-header-title">
                            <h2>Daily Mission</h2>
                            <p>
                                <strong>Tier {tier}:</strong> Complete both
                                requirements to receive {missionRewardTickets}{' '}
                                raffle{' '}
                                {missionRewardTickets === 1
                                    ? 'ticket'
                                    : 'tickets'}
                                .
                            </p>
                            <p className="note">
                                ⚠️ Daily Cut-off: 12:00 PM – 11:30 AM | Keep
                                earning tickets for the Month-End Raffle Draw!
                            </p>
                        </div>

                        <ProgressCard
                            icon="/images/game/jili-rush/JILI-DEPOSIT.webp"
                            title="Today's Deposit"
                            current={depositAmount}
                            needed={depositNeeded}
                            total={depositTarget}
                            percentage={depositPercentage}
                            iconClass="deposit-icon"
                        />

                        {/*
                         * Animation starts from this card
                         * once both missions are complete.
                         */}
                        <ProgressCard
                            ref={missionSourceRef}
                            icon="/images/game/jili-rush/JILI-TAG.webp"
                            title="Today's JILI Turnover"
                            current={turnoverAmount}
                            needed={turnoverNeeded}
                            total={turnoverTarget}
                            percentage={turnoverPercentage}
                            iconClass="jili-icon"
                        />

                        <div className="raffle-tickets" ref={raffleSectionRef}>
                            <RaffleTickets
                                tickets={raffleTickets}
                                earnedToday={earnedToday}
                                targetRef={raffleTargetRef}
                                targetControls={targetControls}
                                rewardFlash={rewardFlash}
                                onShowTicket={handleShowTicket}
                            />
                        </div>
                    </div>

                    <div className="stats-panel-table">
                        <button onClick={handleShowTable}>
                            View all 5 tiers
                        </button>
                    </div>
                </>
            )}
            {showTicket && <TicketHistory onBack={handleCloseTicket} />}

            {/* ========================================= */}
            {/* RAFFLE INFORMATION */}
            {/* ========================================= */}

            <div className="jili-raffle-entries">
                <div className="jili-info-popup">
                    <div className="jili-info-header">
                        <img
                            src="/images/game/jili-rush/JILI-NOTE.webp"
                            alt=""
                        />
                        Raffle Prizes
                    </div>

                    <div className="jili-info-content">
                        <div>
                            <div className="jili-info-item">
                                <p>
                                    Prize is a SPA & Dining experience voucher
                                    with a PHP 25,000 value
                                </p>
                            </div>
                        </div>

                        <div className="jili-info-item--candy">
                            <img
                                src="/images/game/jili-rush/JILI-CANDY-3.webp"
                                alt=""
                            />
                        </div>
                    </div>
                </div>

                <div className="jili-info-popup">
                    <div className="jili-info-header">
                        <img
                            src="/images/game/jili-rush/JILI-NOTE.webp"
                            alt=""
                        />
                        IMPORTANT NOTES
                    </div>

                    <div className="jili-info-content">
                        <div>
                            <div className="jili-info-item">
                                <p>
                                    At the end of the 28-day campaign, all
                                    accumulated raffle tickets will be entered
                                    into a final prize draw.
                                </p>
                            </div>
                        </div>

                        <div className="jili-info-item--candy">
                            <img
                                src="/images/game/jili-rush/JILI-CANDY-2.webp"
                                alt=""
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
